import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faPlay, faFileAlt, faSpinner, faCheckCircle, faTimesCircle, faExclamationTriangle, faChevronDown, faChevronUp, faDownload } from "@fortawesome/pro-regular-svg-icons";
import { toast } from "react-toastify";
import NotificationToast from "../Helpers/NotificationToast";

const GridCallHistory = ({ current_booking }) => {
    const [callLogs, setCallLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedTranscript, setExpandedTranscript] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [fileUrls, setFileUrls] = useState({});

    useEffect(() => {
        if (current_booking?.id) {
            fetchCallLogs();
        }
    }, [current_booking?.id]);

    const fetchCallLogs = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/call-logs`);
            setCallLogs(response.data);
            
            // Fetch file URLs for recordings using view_files endpoint
            const fileIds = response.data
                .filter(log => log.file_id)
                .map(log => log.file_id);
            
            if (fileIds.length > 0) {
                const filesResponse = await axios.post(`${process.env.REACT_APP_API_URL}view_files`, fileIds);
                const urls = {};
                filesResponse.data.forEach(file => {
                    urls[file.id] = file.url;
                });
                setFileUrls(urls);
            }
        } catch (error) {
            console.error("Error fetching call logs:", error);
            toast(<NotificationToast title="Error" message="Failed to load call history" type="error" />);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            completed: { color: "bg-green-100 text-green-800", icon: faCheckCircle, label: "Completed" },
            failed: { color: "bg-red-100 text-red-800", icon: faTimesCircle, label: "Failed" },
            busy: { color: "bg-yellow-100 text-yellow-800", icon: faExclamationTriangle, label: "Busy" },
            "no-answer": { color: "bg-gray-100 text-gray-800", icon: faExclamationTriangle, label: "No Answer" },
            initiated: { color: "bg-blue-100 text-blue-800", icon: faSpinner, label: "Initiated" },
        };

        const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800", icon: faPhone, label: status };

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <FontAwesomeIcon icon={config.icon} className="w-3 h-3" />
                {config.label}
            </span>
        );
    };

    const getRecordingUrl = (fileId) => {
        if (!fileId) return null;
        return fileUrls[fileId] || null;
    };

    const downloadRecording = (fileId) => {
        if (!fileId) {
            toast(<NotificationToast title="No Recording" message="Recording not available yet" type="warning" />);
            return;
        }
        const url = getRecordingUrl(fileId);
        if (url) {
            window.open(url, '_blank');
        } else {
            toast(<NotificationToast title="Error" message="Recording URL not available" type="error" />);
        }
    };

    const toggleTranscript = (logId) => {
        setExpandedTranscript(expandedTranscript === logId ? null : logId);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-2xl" />
            </div>
        );
    }

    if (callLogs.length === 0) {
        return (
            <div className="text-center py-8">
                <FontAwesomeIcon icon={faPhone} className="text-gray-300 text-4xl mb-3" />
                <p className="text-gray-500">No call history yet</p>
                <p className="text-gray-400 text-sm mt-1">Calls made to this applicant will appear here</p>
            </div>
        );
    }

    return (
        <div>
            {/* Accordion Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors mb-4"
            >
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPhone} className="text-gray-600" />
                    <span className="font-medium text-gray-900">Call History</span>
                    {callLogs.length > 0 && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {callLogs.length}
                        </span>
                    )}
                </div>
                <FontAwesomeIcon 
                    icon={isExpanded ? faChevronUp : faChevronDown} 
                    className="text-gray-400"
                />
            </button>

            {/* Accordion Content */}
            {isExpanded && (
                <div className="space-y-4">
            {callLogs.map((log) => (
                <div key={log.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <FontAwesomeIcon icon={faPhone} className="text-blue-600" />
                                <span className="font-medium text-gray-900">{log.staff_name}</span>
                                {getStatusBadge(log.status)}
                            </div>
                            <p className="text-sm text-gray-500">{new Date(log.created_at).toLocaleString()}</p>
                        </div>
                        {log.duration && (
                            <div className="text-right">
                                <p className="text-lg font-semibold text-gray-900">{log.formatted_duration}</p>
                                <p className="text-xs text-gray-500">Duration</p>
                            </div>
                        )}
                    </div>

                    {/* Call Details */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                        <div>
                            <p className="text-gray-500">Agent</p>
                            <p className="font-medium text-gray-900">{log.agent_number}</p>
                        </div>
                        <div>
                            <p className="text-gray-500">Applicant</p>
                            <p className="font-medium text-gray-900">{log.applicant_number}</p>
                        </div>
                    </div>

                    {/* Recording Player */}
                    {log.file_id && (
                        <div className="pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                                <FontAwesomeIcon icon={faFileAlt} className="text-green-600 w-4 h-4" />
                                <span className="text-sm font-medium text-gray-700">Recording</span>
                                {log.recording_duration && (
                                    <span className="text-xs text-gray-500">({log.formatted_recording_duration})</span>
                                )}
                                <button
                                    onClick={() => downloadRecording(log.file_id)}
                                    className="ml-auto text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
                                    title="Download recording"
                                >
                                    <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
                                    Download
                                </button>
                            </div>
                            {getRecordingUrl(log.file_id) ? (
                                <audio
                                    controls
                                    className="w-full"
                                    preload="auto"
                                >
                                    <source src={getRecordingUrl(log.file_id)} type="audio/mpeg" />
                                    Your browser does not support the audio element.
                                </audio>
                            ) : (
                                <div className="text-sm text-gray-500">Loading audio...</div>
                            )}
                        </div>
                    )}

                    {!log.file_id && log.recording_url && (
                        <div className="pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-sm">
                                <FontAwesomeIcon icon={faSpinner} className="w-3 h-3 animate-spin" />
                                <span>Processing recording...</span>
                            </div>
                        </div>
                    )}

                    {/* Transcript Button */}
                    {log.transcription_text && (
                        <div className="pt-3 border-t border-gray-100">
                            <button
                                onClick={() => toggleTranscript(log.id)}
                                className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium w-full justify-center"
                            >
                                <FontAwesomeIcon icon={faFileAlt} className="w-3 h-3" />
                                {expandedTranscript === log.id ? "Hide" : "View"} Transcript
                            </button>
                        </div>
                    )}


                    {/* Transcript */}
                    {expandedTranscript === log.id && log.transcription_text && (
                        <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                            <p className="text-xs font-medium text-purple-700 mb-2">TRANSCRIPT</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{log.transcription_text}</p>
                            {log.transcription_status && (
                                <p className="text-xs text-purple-600 mt-2">Status: {log.transcription_status}</p>
                            )}
                        </div>
                    )}
                </div>
            ))}
                </div>
            )}
        </div>
    );
};

export default GridCallHistory;
